import { useEffect, useState } from 'react';
import { Stack, Group, Button, Text, Title, Card } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { TextInput, Textarea, Switch, Select, NumberInput } from '@repletoryreach/sdk-ui/inputs';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import type { featureFlags } from '@repletoryreach/sdk-api';
import {
  useUpsertFeatureFlag,
  useUpsertOverride,
  useDeleteOverride
} from '../../../../data/useFeatureFlags';
import { FLAG_SCOPES, scopeName } from '../scopes';
import { useT } from '../../../../i18n/I18nProvider';

interface Props {
  flag: featureFlags.FeatureFlag | 'new' | null;
  onClose: () => void;
}

export function EditFlagDrawer({ flag, onClose }: Props) {
  const t = useT();
  const isNew = flag === 'new';
  const existing = isNew || flag === null ? null : flag;
  const open = flag !== null;

  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [owner, setOwner] = useState('');
  const [defaultEnabled, setDefaultEnabled] = useState(false);
  const [rolloutPercent, setRolloutPercent] = useState<number | ''>(0);
  const [tags, setTags] = useState('');
  const [archived, setArchived] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);

  const [overrideScope, setOverrideScope] = useState('org');
  const [overrideTarget, setOverrideTarget] = useState('');
  const [overrideEnabled, setOverrideEnabled] = useState(true);
  const [overrideReason, setOverrideReason] = useState('');
  const [targetError, setTargetError] = useState<string | null>(null);

  const upsert = useUpsertFeatureFlag();
  const upsertOv = useUpsertOverride();
  const deleteOv = useDeleteOverride();

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setKey(existing.key);
      setDescription(existing.description ?? '');
      setOwner(existing.owner ?? '');
      setDefaultEnabled(existing.default_enabled);
      setRolloutPercent(existing.rollout_percent);
      setTags(existing.tags.join(', '));
      setArchived(existing.archived);
    } else {
      setKey('');
      setDescription('');
      setOwner('');
      setDefaultEnabled(false);
      setRolloutPercent(0);
      setTags('');
      setArchived(false);
    }
    setKeyError(null);
  }, [open, existing]);

  async function save() {
    if (!key.trim()) {
      // Field-level, where the eye already is. This used to be a toast in the
      // corner about a field the operator was looking straight at.
      setKeyError(t('settings.flags.form.errors.keyRequired'));
      return;
    }
    setKeyError(null);

    try {
      await upsert.mutateAsync({
        key: key.trim(),
        description: description || null,
        owner: owner || null,
        default_enabled: defaultEnabled,
        rollout_percent: rolloutPercent === '' ? 0 : rolloutPercent,
        tags: tags.split(',').map((s) => s.trim()).filter(Boolean),
        archived
      });
      notifySuccess(
        t('settings.flags.toast.saved', { key }),
        t(isNew ? 'settings.flags.toast.createdTitle' : 'settings.flags.toast.savedTitle')
      );
      if (isNew) onClose();
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.flags.toast.saveFailed'));
    }
  }

  async function addOverride() {
    if (!existing) return;
    if (!overrideTarget.trim()) {
      setTargetError(t('settings.flags.form.errors.targetRequired'));
      return;
    }
    setTargetError(null);
    try {
      await upsertOv.mutateAsync({
        key: existing.key,
        scope_code: overrideScope,
        scope_target: overrideTarget.trim(),
        enabled: overrideEnabled,
        reason: overrideReason || null
      });
      notifySuccess(t('settings.flags.toast.overrideSaved'), existing.key);
      setOverrideTarget('');
      setOverrideReason('');
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.flags.toast.saveFailed'));
    }
  }

  /*
   * Removing an override is how a kill switch for one tenant is taken away, so
   * it confirms and names the scope and target rather than removing a row on a
   * single misclick.
   */
  function removeOverride(o: featureFlags.FeatureFlag['overrides'][number]) {
    if (!existing) return;
    openConfirmModal({
      title: t('settings.flags.removeOverride.title'),
      children: (
        <p style={{ margin: 0 }}>
          {t('settings.flags.removeOverride.body', {
            scope: scopeName(o.scope_id),
            target: o.scope_target,
            key: existing.key,
            state: defaultEnabled ? t('settings.flags.on') : t('settings.flags.off')
          })}
        </p>
      ),
      labels: { confirm: t('action.remove'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteOv.mutateAsync({ key: existing.key, id: o.id });
          notifySuccess(t('settings.flags.toast.overrideRemoved'), existing.key);
        } catch (e) {
          notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.flags.toast.saveFailed'));
        }
      }
    });
  }

  const overrideCount = existing?.overrides.length ?? 0;

  return (
    <Drawer
      opened={open}
      onClose={onClose}
      title={isNew ? t('settings.flags.form.newTitle') : t('settings.flags.form.editTitle', { key: existing?.key ?? '' })}
      position="right"
      size="md"
    >
      <Stack gap="md">
        <TextInput
          label={t('settings.flags.form.key')}
          description={t('settings.flags.form.keyHint')}
          value={key}
          onChange={(e) => { setKey(e.currentTarget.value); if (keyError) setKeyError(null); }}
          error={keyError ?? undefined}
          required
          disabled={!isNew}
        />
        <Textarea
          label={t('common.description')}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          minRows={2}
        />
        <TextInput
          label={t('settings.flags.form.owner')}
          placeholder={t('settings.flags.form.ownerPlaceholder')}
          value={owner}
          onChange={(e) => setOwner(e.currentTarget.value)}
        />
        <Group gap="md" grow>
          <Switch
            label={t('settings.flags.form.defaultEnabled')}
            checked={defaultEnabled}
            onChange={(next) => setDefaultEnabled(next)}
          />
          {/* A number input rather than free text: "50%" and "0.5" used to be
              accepted by the field and rejected by a toast after Save. */}
          <NumberInput
            label={t('settings.flags.form.rollout')}
            description={t('settings.flags.form.rolloutHint')}
            value={rolloutPercent}
            onChange={(v) => setRolloutPercent(v)}
            min={0}
            max={100}
          />
        </Group>
        <TextInput
          label={t('settings.flags.form.tags')}
          description={t('settings.flags.form.tagsHint')}
          value={tags}
          onChange={(e) => setTags(e.currentTarget.value)}
        />
        <Switch
          label={t('settings.flags.form.archived')}
          description={t('settings.flags.form.archivedHint')}
          checked={archived}
          onChange={(next) => setArchived(next)}
        />

        {/* What saving this actually does, above the button that does it. */}
        <Alert color="info">
          {t('settings.flags.form.blastRadius', {
            state: defaultEnabled ? t('settings.flags.on') : t('settings.flags.off'),
            percent: rolloutPercent === '' ? 0 : rolloutPercent
          })}
          {overrideCount > 0 && ` ${t('settings.flags.form.blastRadiusOverrides', { count: overrideCount })}`}
        </Alert>

        <Group justify="flex-end" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
          <Button variant="default" onClick={onClose}>{t('action.close')}</Button>
          <Button onClick={save} loading={upsert.isPending}>{t('action.save')}</Button>
        </Group>

        {existing && (
          <Stack gap="md" style={{ paddingTop: 12 }}>
            <Title order={6}>{t('settings.flags.scopedOverrides')}</Title>
            {existing.overrides.length === 0 && (
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.flags.noOverrides')}</Text>
            )}
            {existing.overrides.map((o) => (
              <Group key={o.id} justify="space-between" style={{ padding: 8, border: '1px solid var(--rr-color-border)', borderRadius: 6 }}>
                <Stack gap="xs">
                  {/* The card list showed the scope and this list dropped it,
                      so the same override read differently in two places. */}
                  <Text role="bodySmall">
                    <code>{scopeName(o.scope_id)}:{o.scope_target}</code> · {o.enabled ? t('settings.flags.on') : t('settings.flags.off')}
                  </Text>
                  {o.reason && <Text role="caption" c="var(--rr-color-text-muted)">{o.reason}</Text>}
                </Stack>
                <Button
                  size="xs"
                  variant="subtle"
                  color="red"
                  loading={deleteOv.isPending && deleteOv.variables?.id === o.id}
                  onClick={() => removeOverride(o)}
                >
                  {t('action.remove')}
                </Button>
              </Group>
            ))}

            {/* The add form used to live inside an info Alert, which made a
                form look like a notice. */}
            <Card withBorder>
              <Stack gap="md">
                <Text role="bodySmall" fw={600}>{t('settings.flags.addOverride')}</Text>
                <Group gap="sm" grow>
                  <Select
                    size="sm"
                    label={t('users.filters.scope')}
                    value={overrideScope}
                    onChange={(e) => setOverrideScope(e.currentTarget.value)}
                    data={FLAG_SCOPES.map((s) => ({ value: s.code, label: t(s.labelKey) }))}
                  />
                  <TextInput
                    size="sm"
                    label={t('settings.flags.form.target')}
                    placeholder={t('settings.flags.form.targetPlaceholder')}
                    value={overrideTarget}
                    onChange={(e) => { setOverrideTarget(e.currentTarget.value); if (targetError) setTargetError(null); }}
                    error={targetError ?? undefined}
                  />
                </Group>
                <Group gap="md">
                  <Switch
                    label={t('common.enabled')}
                    checked={overrideEnabled}
                    onChange={(next) => setOverrideEnabled(next)}
                  />
                  <TextInput
                    label={t('settings.flags.form.reason')}
                    placeholder={t('settings.flags.form.reasonPlaceholder')}
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.currentTarget.value)}
                    style={{ flex: 1 }}
                  />
                </Group>
                <Group justify="flex-end">
                  <Button size="xs" onClick={addOverride} loading={upsertOv.isPending}>{t('settings.flags.addOverride')}</Button>
                </Group>
              </Stack>
            </Card>
          </Stack>
        )}
      </Stack>
    </Drawer>
  );
}
